import { apiSlice } from './apiSlice';

// Types
export interface DataAgent {
  data_agent_id: string;
  name: string;
  customer_id: string;
  client_id: string;
  description?: string;
  instructions?: string;
  system_prompt?: string;
  max_tokens?: number;
  temperature?: number;
  is_active: boolean;
  is_published: boolean;
  created_at?: string;
  updated_at?: string;
  icon?: string;
}

interface GetDataAgentsParams {
  customer_id?: string;
  client_id?: string;
  is_active?: boolean;
  is_published?: boolean;
}

interface CreateDataAgentPayload {
  name: string;
  customer_id: string;
  client_id: string;
  description?: string;
  instructions?: string;
  system_prompt?: string;
  max_tokens?: number;
  temperature?: number;
  is_active?: boolean;
  is_published?: boolean;
}

interface UpdateDataAgentPayload extends Partial<CreateDataAgentPayload> {
  data_agent_id: string;
}

// Source types
interface DataAgentSourcePayload {
  customer_id: string;
  client_id: string;
  source_type?: string;
  semantic_model_id?: string | null;
  lakehouse_id?: string | null;
  source_id?: string | null;
  data_connection_id?: string | null;
  data_table_id?: string | null;
  column_descriptions?: unknown;
  source_instructions?: string | null;
  processed_schema?: unknown;
  is_active?: boolean;
}

export interface DataAgentSource {
  // core fields
  data_agent_source_id: string | null;
  source_type: string;
  semantic_model_id: string | null;
  source_id: string | null;
  data_table_id: string | null;
  column_descriptions: string | null;
  source_instructions: string | null;
  processed_schema: string | null;
  is_active: boolean;
  created_id: string | null;
  created_at: string | null;
  updated_id: string | null;
  updated_at: string | null;

  data_connector?: DataAgentSourceDataConnector | null;
  lakehouse?: DataAgentSourceLakehouse | null;
}

export interface DataAgentSourceDataConnector {
  data_connection_id: string | null;
  name: string | null;
  description: string | null;
  customer_id: string | null;
  client_id: string | null;
  connection_type: string | null;
  host: string | null;
  port: number | null;
  database_name: string | null;
  username: string | null;
  password: string | null;
  connection_string: string | null;
  storage_path: string | null;
  medallion_enabled: boolean | null;
  connection_settings: string | null;
  connection_status: string | null;
  last_tested_at: string | null;
  last_error: string | null;
  created_at: string | null;
  updated_at: string | null;
  created_by: string | null;
  updated_by: string | null;
  status: number | null;
  auth_type: string | null;
}

export interface DataAgentSourceLakehouse {
  lakehouse_id: string | null;
  name: string | null;
  description: string | null;
  code: string | null;
  customer_id: string | null;
  client_id: string | null;
  status: number | null;
  created_id: string | null;
  created_at: string | null;
  updated_id: string | null;
  updated_at: string | null;
}
interface DataAgentSourcesResponse {
  data_agent_sources: DataAgentSource[];
}
// POST /data_agents/:data_agent_id/sources
interface CreateDataAgentSourceArg extends DataAgentSourcePayload {
  data_agent_id: string;
}

interface CreateDataAgentSourceResponse {
  message: string;
  data_agent_source_id: string; // as returned by your backend
  data_agent: DataAgent;
}

// PUT /data_agents/sources/:data_agent_source_id
interface UpdateDataAgentSourceArg extends Partial<DataAgentSourcePayload> {
  data_agent_source_id: string;
}

interface UpdateDataAgentSourceResponse {
  message: string;
  data_agent: DataAgent;
}

export interface DataAgentExampleQuery {
  data_agent_example_query_id: string;
  data_agent_id: string;
  question: string;
  query: string;
  query_language?: string | null;
  description?: string | null;
  expected_result?: string | null;
  is_active: boolean;
  created_id?: string | null;
  created_at?: string | null;
  updated_id?: string | null;
  updated_at?: string | null;
}

interface GetDataAgentExampleQueriesParams {
  data_agent_id?: string;
  data_agent_example_query_id?: string;
  customer_id?: string;
  client_id?: string;
}

interface GetDataAgentExampleQueriesResponse {
  data_agent_example_queries: DataAgentExampleQuery[];
}

interface CreateDataAgentExampleQueryPayload {
  customer_id: string;
  client_id: string;
  question: string;
  query: string;
  query_language?: string;
  description?: string;
  expected_result?: string;
  is_active?: boolean;
}

interface CreateDataAgentExampleQueryArg extends CreateDataAgentExampleQueryPayload {
  data_agent_id: string; // ✅ Added for URL parameter
}

interface CreateDataAgentExampleQueryResponse {
  message: string;
  data_agent_id: string;
  example_query: DataAgentExampleQuery;
}

interface UpdateDataAgentExampleQueryPayload {
  customer_id: string | null;
  client_id: string | null;
  data_agent_id: string;
  question: string;
  query: string;
  query_language?: string;
  description?: string;
  expected_result?: string;
  is_active?: boolean;
}

interface UpdateDataAgentExampleQueryArg extends UpdateDataAgentExampleQueryPayload {
  data_agent_example_query_id: string; // ✅ Added for URL parameter
}

interface UpdateDataAgentExampleQueryResponse {
  message: string;
  data_agent_id: string;
  example_query: DataAgentExampleQuery;
}

export const dataAgentApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // GET /api/v1/data_agents - Get all data agents with filters
    getDataAgents: builder.query<DataAgent[], GetDataAgentsParams | void>({
      query: (params) => ({
        url: `/api/v1/data_agents`,
        method: 'GET',
        params: params || {},
      }),
      transformResponse: (response: { data_agents: DataAgent[] }) => response.data_agents,
      providesTags: ['DataAgent'],
    }),

    // GET /api/v1/data_agents/:data_agent_id - Get single data agent
    getDataAgentById: builder.query<DataAgent, string>({
      query: (dataAgentId) => ({
        url: `/api/v1/data_agents/${dataAgentId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data_agent: DataAgent }) => response.data_agent,
      providesTags: ['DataAgent'],
    }),

    // POST /api/v1/data_agents - Create new data agent
    createDataAgent: builder.mutation<{ message: string; data_agent: DataAgent }, CreateDataAgentPayload>({
      query: (newDataAgent) => ({
        url: `/api/v1/data_agents`,
        method: 'POST',
        body: newDataAgent,
      }),
      invalidatesTags: ['DataAgent', 'DataAgentSource'],
    }),

    // PUT /api/v1/data_agents/:data_agent_id - Update data agent
    updateDataAgent: builder.mutation<{ message: string; data_agent: DataAgent }, UpdateDataAgentPayload>({
      query: ({ data_agent_id, ...updatedDataAgent }) => ({
        url: `/api/v1/data_agents/${data_agent_id}`,
        // url: `/api/v1/data_agents`,
        method: 'PUT',
        body: updatedDataAgent,
      }),
      invalidatesTags: ['DataAgent', 'DataAgentSource', 'VersionLog'],
    }),

    updateDataAgentSource: builder.mutation<UpdateDataAgentSourceResponse, UpdateDataAgentSourceArg>({
      query: ({ data_agent_source_id, ...body }) => ({
        url: `/api/v1/data_agents/sources/${data_agent_source_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['DataAgent', 'DataAgentSource'],
    }),

    createDataAgentSource: builder.mutation<
      CreateDataAgentSourceResponse,
      Omit<CreateDataAgentSourceArg, 'data_agent_id'>
    >({
      query: (body) => ({
        url: `/api/v1/data_agents/sources`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DataAgent', 'DataAgentSource'],
    }),
    getDataAgentSources: builder.query<
      DataAgentSourcesResponse,
      {
        data_agent_id?: string;
        customer_id?: string;
        client_id?: string;
        source_id?: string;
        data_agent_source_id?: string;
        include_lakehouses_and_data_connector?: boolean;
      }
    >({
      query: ({
        data_agent_id,
        customer_id,
        client_id,
        source_id,
        data_agent_source_id,
        include_lakehouses_and_data_connector,
      } = {}) => {
        const params = new URLSearchParams();

        if (data_agent_id) params.set('data_agent_id', data_agent_id);
        if (customer_id) params.set('customer_id', customer_id);
        if (client_id) params.set('client_id', client_id);
        if (source_id) params.set('source_id', source_id);
        if (data_agent_source_id) params.set('data_agent_source_id', data_agent_source_id);
        if (include_lakehouses_and_data_connector !== undefined) {
          params.set('include_lakehouses_and_data_connector', include_lakehouses_and_data_connector ? 'true' : 'false');
        }

        const queryString = params.toString();
        return {
          url: `/api/v1/data_agents/sources${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['DataAgentSource'],
    }),

    createDataAgentExampleQuery: builder.mutation<CreateDataAgentExampleQueryResponse, CreateDataAgentExampleQueryArg>({
      query: ({ data_agent_id, ...body }) => ({
        url: `/api/v1/data_agents/${data_agent_id}/example-queries`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'DataAgentExampleQuery', id: 'LIST' }],
    }),
    updateDataAgentExampleQuery: builder.mutation<UpdateDataAgentExampleQueryResponse, UpdateDataAgentExampleQueryArg>({
      query: ({ data_agent_example_query_id, ...body }) => ({
        url: `/api/v1/data_agents/example-queries/${data_agent_example_query_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'DataAgentExampleQuery', id: arg.data_agent_example_query_id },
        { type: 'DataAgentExampleQuery', id: 'LIST' },
      ],
    }),
    autoConfigureSource: builder.mutation<
      ReadableStream,
      { data_agent_source_id: string; customer_id: string; client_id: string }
    >({
      query: ({ data_agent_source_id, customer_id, client_id }) => ({
        url: `/api/v1/data_agents/sources/${data_agent_source_id}/auto-configure`,
        method: 'POST',
        body: { customer_id, client_id },
        responseHandler: (response: Response): Promise<ReadableStream> =>
          Promise.resolve(response.body as ReadableStream),
      }),
      invalidatesTags: ['DataAgentSource'],
    }),
    getDataAgentExampleQueries: builder.query<DataAgentExampleQuery[], GetDataAgentExampleQueriesParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();

        if (params?.data_agent_id) searchParams.set('data_agent_id', params.data_agent_id);
        if (params?.data_agent_example_query_id)
          searchParams.set('data_agent_example_query_id', params.data_agent_example_query_id);
        if (params?.customer_id) searchParams.set('customer_id', params.customer_id);
        if (params?.client_id) searchParams.set('client_id', params.client_id);

        const queryString = searchParams.toString();
        return {
          url: `/api/v1/data_agents/example-queries${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      transformResponse: (response: GetDataAgentExampleQueriesResponse) => response.data_agent_example_queries,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ data_agent_example_query_id }) => ({
                type: 'DataAgentExampleQuery' as const,
                id: data_agent_example_query_id,
              })),
              { type: 'DataAgentExampleQuery' as const, id: 'LIST' },
            ]
          : [{ type: 'DataAgentExampleQuery' as const, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetDataAgentsQuery,
  useGetDataAgentByIdQuery,
  useCreateDataAgentMutation,
  useUpdateDataAgentMutation,
  useGetDataAgentSourcesQuery,
  useCreateDataAgentSourceMutation,
  useUpdateDataAgentSourceMutation,
  useGetDataAgentExampleQueriesQuery,
  useCreateDataAgentExampleQueryMutation,
  useUpdateDataAgentExampleQueryMutation,
  useAutoConfigureSourceMutation,
} = dataAgentApiSlice;
